/**
 * Keeps ordinary site navigation resilient when another UI layer briefly
 * intercepts the click. The theme has several legitimate click handlers for
 * drawers, filters, tabs, product forms, and cards; this guard only applies to
 * plain same-window anchor navigation.
 */
(function () {
  const CONTROL_SKIP_SELECTOR = [
    '[on\\:click]',
    '[on\\:submit]',
    'button',
    'input',
    'label',
    'select',
    'textarea',
    'summary',
    'form',
    '[role="button"]',
    '[role="tab"]',
    'product-form-component',
    'quick-add-component',
    '.bl-cart-recommendation__form',
    '.bl-product-card__form',
    '.quick-add',
    '.quick-add__button',
    '.variant-option',
  ].join(',');

  const LINK_SKIP_SELECTOR = [
    '[data-bl-filter-chip]',
    '[data-no-navigation]',
    '[on\\:click]',
    '[href^="#"]',
  ].join(',');

  const CONTAINER_SKIP_SELECTOR = [
    '[data-no-navigation]',
    'dialog',
    'theme-drawer',
    'cart-drawer-component',
    'collection-links-component',
    'facets-form',
    'predictive-search-component',
  ].join(',');

  /**
   * @param {Element} target
   * @param {HTMLAnchorElement | null} link
   */
  function hasSkippedControlBeforeLink(target, link) {
    let node = target;

    while (node && node !== link) {
      if (node.matches(CONTROL_SKIP_SELECTOR)) return true;
      node = node.parentElement;
    }

    return false;
  }

  /**
   * @param {Element} target
   * @returns {HTMLAnchorElement | null}
   */
  function getProductCardLink(target) {
    const productCard = target.closest('product-card:not([data-no-navigation])');
    if (!productCard) return null;

    let node = target;
    while (node && node !== productCard) {
      if (node.matches(CONTROL_SKIP_SELECTOR)) return null;
      node = node.parentElement;
    }

    return /** @type {HTMLAnchorElement | null} */ (
      productCard.querySelector('a[ref="productCardLink"][href], a.product-card__link[href]')
    );
  }

  /**
   * @param {Element} target
   * @returns {HTMLAnchorElement | null}
   */
  function getNavigationLink(target) {
    const link = target.closest('a[href]');

    if (link instanceof HTMLAnchorElement) {
      return hasSkippedControlBeforeLink(target, link) ? null : link;
    }

    return getProductCardLink(target);
  }

  /**
   * @param {HTMLAnchorElement} link
   * @param {MouseEvent} event
   */
  function shouldGuardNavigation(link, event) {
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (!link.href || link.target || link.hasAttribute('download')) return false;
    if (link.matches(LINK_SKIP_SELECTOR)) return false;
    if (link.closest(CONTAINER_SKIP_SELECTOR)) return false;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return false;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;

    const currentUrl = new URL(window.location.href);
    const isSamePageHash =
      url.pathname === currentUrl.pathname && url.search === currentUrl.search && url.hash && url.hash !== currentUrl.hash;

    return !isSamePageHash;
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!(event instanceof MouseEvent)) return;
      if (!(event.target instanceof Element)) return;

      const link = getNavigationLink(event.target);
      if (!link) return;
      if (!shouldGuardNavigation(link, event)) return;

      const destination = link.href;
      const startingUrl = window.location.href;

      setTimeout(() => {
        if (window.location.href !== startingUrl) return;

        window.location.assign(destination);
      }, event.defaultPrevented ? 0 : 120);
    },
    true
  );
})();
